// src/components/homeComponents/WeDoParallax.jsx
import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import {
  FaCode,
  FaHeart,
  FaChartLine,
  FaPlay,
  FaTerminal,
  FaPalette,
  FaStar,
} from "react-icons/fa";

gsap.registerPlugin(ScrollTrigger);

/*
  BETTER ___ — the payoff of the title card.
  Each service word rolls in like a slot machine (3D flip + blur),
  brings its own accent color, its own tagline, its own icon burst,
  and re-lights the whole room. GSAP pin, no CSS sticky.
*/
const WORDS = [
  {
    text: "Websites",
    accent: "#9281dd",
    tagline: "that load fast and sell faster.",
    Icon: FaCode,
  },
  {
    text: "Social Media",
    accent: "#e6608a",
    tagline: "that stops thumbs mid-scroll.",
    Icon: FaHeart,
  },
  {
    text: "Marketing",
    accent: "#f2b134",
    tagline: "that pays for itself.",
    Icon: FaChartLine,
  },
  {
    text: "Content",
    accent: "#e05656",
    tagline: "people actually finish watching.",
    Icon: FaPlay,
  },
  {
    text: "Web Apps",
    accent: "#4ea9d6",
    tagline: "built end-to-end, no templates.",
    Icon: FaTerminal,
  },
  {
    text: "Branding",
    accent: "#8fc15a",
    tagline: "people remember by color alone.",
    Icon: FaPalette,
  },
  {
    text: "Stuff!",
    accent: "#b9c5fb",
    tagline: "honestly, just ask us.",
    Icon: FaStar,
  },
];

const BURST_COUNT = 8;

export default function WeDoParallax() {
  const sectionRef = useRef(null);
  const pinRef = useRef(null);
  const auraRef = useRef(null);
  const wordRefs = useRef([]);
  const tagRefs = useRef([]);
  const burstRefs = useRef([]);
  const underlineRef = useRef(null);
  const ghostRef = useRef(null);
  const lastIdx = useRef(-1);
  const [active, setActive] = useState(0);

  const swapTo = (idx, prev) => {
    const inWord = wordRefs.current[idx];
    const outWord = prev >= 0 ? wordRefs.current[prev] : null;
    const inTag = tagRefs.current[idx];
    const accent = WORDS[idx].accent;
    const down = prev < idx; // scroll direction

    // ── AUTHORITATIVE RESET ─────────────────────────────────────────
    // Fast scrubbing can skip several indices in one frame; any word
    // that was mid-tween would otherwise finish its animation and pile
    // up on screen. So: kill every in-flight word/tag tween, then hard-
    // hide everything except the incoming word (and the outgoing one,
    // which gets a proper exit animation below).
    wordRefs.current.forEach((el, i) => {
      if (!el) return;
      gsap.killTweensOf(el);
      if (i !== idx && el !== outWord) {
        gsap.set(el, {
          yPercent: 105,
          rotateX: 0,
          opacity: 0,
          filter: "blur(0px)",
        });
      }
    });
    tagRefs.current.forEach((el, i) => {
      if (!el) return;
      gsap.killTweensOf(el);
      if (i !== idx) gsap.set(el, { opacity: 0, y: 0 });
    });
    // ────────────────────────────────────────────────────────────────

    // slot-machine roll
    if (outWord) {
      gsap.to(outWord, {
        yPercent: down ? -105 : 105,
        rotateX: down ? 70 : -70,
        opacity: 0,
        filter: "blur(6px)",
        duration: 0.45,
        ease: "power3.in",
        overwrite: "auto",
      });
    }
    gsap.fromTo(
      inWord,
      {
        yPercent: down ? 105 : -105,
        rotateX: down ? -70 : 70,
        opacity: 0,
        filter: "blur(6px)",
      },
      {
        yPercent: 0,
        rotateX: 0,
        opacity: 1,
        filter: "blur(0px)",
        duration: 0.55,
        ease: "power3.out",
        delay: 0.08,
        overwrite: "auto",
      },
    );

    // tagline in (all others were hard-hidden in the reset above)
    gsap.fromTo(
      inTag,
      { opacity: 0, y: 14 },
      { opacity: 1, y: 0, duration: 0.5, ease: "power2.out", delay: 0.2 },
    );

    // underline sweep in the new accent
    if (underlineRef.current) {
      gsap.fromTo(
        underlineRef.current,
        { scaleX: 0, transformOrigin: down ? "left" : "right" },
        {
          scaleX: 1,
          backgroundColor: accent,
          duration: 0.6,
          ease: "power3.out",
          delay: 0.15,
          overwrite: "auto",
        },
      );
    }

    // room re-light
    if (auraRef.current) {
      gsap.to(auraRef.current, {
        background: `radial-gradient(circle at 62% 50%, ${accent}26 0%, #08040c 62%)`,
        duration: 0.7,
        overwrite: "auto",
      });
    }

    // ghost index flick
    if (ghostRef.current) {
      ghostRef.current.textContent = `0${idx + 1}`;
      gsap.fromTo(
        ghostRef.current,
        { opacity: 0, y: down ? 40 : -40 },
        {
          opacity: 1,
          y: 0,
          duration: 0.5,
          ease: "power2.out",
          overwrite: "auto",
        },
      );
    }

    // icon burst in accent color
    const burst = burstRefs.current[idx];
    if (burst) {
      const icons = burst.querySelectorAll(".b-ico");
      icons.forEach((ic, k) => {
        const angle =
          (k / BURST_COUNT) * Math.PI * 2 + gsap.utils.random(-0.25, 0.25);
        const dist = gsap.utils.random(70, 150);
        gsap.fromTo(
          ic,
          { x: 0, y: 0, opacity: 0.9, scale: gsap.utils.random(0.5, 1) },
          {
            x: Math.cos(angle) * dist,
            y: Math.sin(angle) * dist,
            opacity: 0,
            scale: 0.15,
            rotate: gsap.utils.random(-90, 90),
            duration: gsap.utils.random(0.7, 1.1),
            ease: "power3.out",
            overwrite: "auto",
          },
        );
      });
    }
  };

  useEffect(() => {
    // initial states: only word 0 visible
    wordRefs.current.forEach((el, i) => {
      if (!el) return;
      gsap.set(el, {
        yPercent: i === 0 ? 0 : 105,
        opacity: i === 0 ? 1 : 0,
        rotateX: 0,
      });
    });
    tagRefs.current.forEach((el, i) => {
      if (!el) return;
      gsap.set(el, { opacity: i === 0 ? 1 : 0 });
    });

    let st;
    const t = setTimeout(() => {
      st = ScrollTrigger.create({
        trigger: sectionRef.current,
        start: "top top",
        end: `+=${WORDS.length * 28}%`,
        pin: pinRef.current,
        scrub: 1,
        anticipatePin: 1,
        invalidateOnRefresh: true,
        onUpdate: (self) => {
          const idx = Math.min(
            WORDS.length - 1,
            Math.floor(self.progress * WORDS.length),
          );
          if (idx !== lastIdx.current) {
            const prev = lastIdx.current;
            lastIdx.current = idx;
            setActive(idx);
            swapTo(idx, prev);
          }
        },
      });
      ScrollTrigger.refresh();
    }, 120);

    return () => {
      clearTimeout(t);
      if (st) st.kill();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Section ref={sectionRef}>
      <Pin ref={pinRef}>
        <div className="aura" ref={auraRef} />
        <span className="ghost" ref={ghostRef}>
          01
        </span>

        <Row>
          <span className="static">Better</span>

          <div className="slot">
            {WORDS.map((w, i) => (
              <span
                className="word"
                key={w.text}
                ref={(el) => (wordRefs.current[i] = el)}
                style={{ color: w.accent }}
              >
                {w.text}
                <span
                  className="burst"
                  ref={(el) => (burstRefs.current[i] = el)}
                >
                  {Array.from({ length: BURST_COUNT }).map((_, k) => (
                    <w.Icon className="b-ico" key={k} />
                  ))}
                </span>
              </span>
            ))}
            <span className="underline" ref={underlineRef} />
          </div>
        </Row>

        <div className="tags">
          {WORDS.map((w, i) => (
            <p key={w.text} ref={(el) => (tagRefs.current[i] = el)}>
              {w.tagline}
            </p>
          ))}
        </div>

        <Ticks>
          {WORDS.map((w, i) => (
            <span
              key={w.text}
              className={i === active ? "on" : ""}
              style={{ background: i === active ? w.accent : undefined }}
            />
          ))}
        </Ticks>
      </Pin>
    </Section>
  );
}

const Section = styled.section`
  position: relative;
  background: #08040c;
`;

const Pin = styled.div`
  height: 100vh;
  width: 100%;
  overflow: hidden;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  .aura {
    position: absolute;
    inset: 0;
    background: radial-gradient(circle at 62% 50%, #9281dd26 0%, #08040c 62%);
    pointer-events: none;
  }

  .ghost {
    position: absolute;
    right: 4%;
    bottom: -4vh;
    font-family: "Readex Pro", sans-serif;
    font-weight: 200;
    font-size: clamp(12rem, 30vw, 26rem);
    line-height: 1;
    color: #ffffff06;
    pointer-events: none;
    user-select: none;
  }

  .tags {
    position: relative;
    height: 2em;
    margin-top: 1.6em;
    width: 100%;
    text-align: center;
    p {
      position: absolute;
      inset: 0;
      margin: auto;
      padding: 0 1.2em;
      color: #8a8595;
      font-size: clamp(0.95rem, 1.6vw, 1.25rem);
      letter-spacing: 0.03em;
      opacity: 0;
    }
  }
`;

const Row = styled.div`
  position: relative;
  z-index: 2;
  display: flex;
  align-items: flex-start;
  gap: 2.2vw;
  font-family: "Readex Pro", sans-serif;
  font-size: clamp(2.6rem, 8vw, 8rem);
  line-height: 1.15;
  perspective: 900px;

  .static {
    font-weight: 200;
    color: #ffffff;
    flex-shrink: 0;
  }

  .slot {
    position: relative;
    height: 1.25em;
    min-width: 5.4em;
    overflow: visible;

    .word {
      position: absolute;
      left: 0;
      top: 0;
      white-space: nowrap;
      font-weight: 600;
      will-change: transform, opacity, filter;
      transform-style: preserve-3d;

      .burst {
        position: absolute;
        left: 50%;
        top: 50%;
        width: 0;
        height: 0;
        pointer-events: none;
        .b-ico {
          position: absolute;
          font-size: clamp(0.9rem, 1.6vw, 1.4rem);
          color: currentColor;
          opacity: 0;
        }
      }
    }

    .underline {
      position: absolute;
      left: 0;
      bottom: -0.06em;
      width: 100%;
      height: 0.055em;
      border-radius: 4px;
      background: #9281dd;
      transform: scaleX(0);
    }
  }

  @media (max-width: 700px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 0;
    padding-right: 30%;
    .slot {
      min-width: 0;
      width: 100%;
      text-align: left;
      .word {
        left: 0;
        right: 0;
        text-align: center;
      }
    }
  }
`;

const Ticks = styled.div`
  position: absolute;
  left: 5%;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  flex-direction: column;
  gap: 10px;
  z-index: 3;
  span {
    width: 5px;
    height: 5px;
    border-radius: 50%;
    background: #2c2342;
    transition: 0.4s;
    &.on {
      height: 24px;
      border-radius: 4px;
    }
  }
  @media (max-width: 700px) {
    left: 4%;
  }
`;
